/**
 * widgets/Drawer.ts · 通用右侧抽屉 (复用 v200 TN_DRAWER)
 *
 * 解决场景:
 *   - 列表点击行不跳页, 右侧预览 480px · 列表 filter 完整保留
 *   - 表单 modal · ESC 关 / backdrop click 关
 *   - 详情快速查看不丢上下文
 *
 * 用法:
 *   import { openDrawer } from '@widgets/Drawer';
 *   openDrawer({
 *     title: 'SO-2026-0001 详情',
 *     content: '<div>...HTML...</div>' 或 HTMLElement,
 *     footer: '<button>关闭</button>',
 *     onClose: () => console.log('closed'),
 *     width: 480,  // 默认 480
 *   });
 */

interface DrawerOpts {
  title: string;
  content: string | HTMLElement;
  footer?: string | HTMLElement;
  width?: number;
  onClose?: () => void;
}

const OVERLAY_ID = 'tn-drawer-overlay';
const PANEL_ID = 'tn-drawer-panel';

let _currentClose: (() => void) | null = null;

export function openDrawer(opts: DrawerOpts): void {
  // 已有 drawer · 先关掉
  closeDrawer();

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(2px);
    z-index: 99997;
    animation: tnDrawerFade 0.2s ease-out;
  `;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeDrawer();
  });

  const panel = document.createElement('div');
  panel.id = PANEL_ID;
  const width = opts.width || 480;
  panel.style.cssText = `
    position: fixed; top: 0; right: 0; bottom: 0;
    width: ${width}px; max-width: 95vw;
    background: #fff;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
    z-index: 99998;
    display: flex; flex-direction: column;
    animation: tnDrawerSlideIn 0.22s cubic-bezier(0.25, 0.8, 0.25, 1);
  `;

  // Header
  const header = document.createElement('div');
  header.className = 'flex items-center justify-between px-5 py-4 border-b border-gray-200';
  header.innerHTML = `
    <h2 class="text-base font-bold text-gray-900">${escapeHtml(opts.title)}</h2>
    <button class="tn-drawer-close p-1 rounded hover:bg-gray-100 text-gray-500" title="关闭 (Esc)">
      <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor"><path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/></svg>
    </button>
  `;
  panel.appendChild(header);

  // Body
  const body = document.createElement('div');
  body.className = 'flex-1 overflow-y-auto px-5 py-4';
  body.style.cssText = 'overscroll-behavior: contain;';
  if (typeof opts.content === 'string') {
    body.innerHTML = opts.content;
  } else {
    body.appendChild(opts.content);
  }
  panel.appendChild(body);

  // Footer (optional)
  if (opts.footer) {
    const footer = document.createElement('div');
    footer.className = 'px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2';
    if (typeof opts.footer === 'string') {
      footer.innerHTML = opts.footer;
    } else {
      footer.appendChild(opts.footer);
    }
    panel.appendChild(footer);
  }

  document.body.appendChild(overlay);
  document.body.appendChild(panel);

  // Close handlers
  const close = () => closeDrawer();
  panel.querySelector('.tn-drawer-close')?.addEventListener('click', close);
  _currentClose = () => {
    overlay.remove();
    panel.remove();
    if (opts.onClose) opts.onClose();
    _currentClose = null;
  };

  // Esc to close
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);

  // Inject CSS once
  injectCss();
}

export function closeDrawer(): void {
  if (_currentClose) _currentClose();
}

function injectCss(): void {
  if (document.getElementById('tn-drawer-css')) return;
  const style = document.createElement('style');
  style.id = 'tn-drawer-css';
  style.textContent = `
    @keyframes tnDrawerFade {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes tnDrawerSlideIn {
      from { transform: translateX(100%); }
      to   { transform: translateX(0); }
    }
  `;
  document.head.appendChild(style);
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
